/**
 * Curve calculation modes.
 * - "absolute": legacy behaviour. `curve` is the exact pixel deflection of the
 *   center seat from the straight line. Kept so previously saved rows render
 *   identically when reopened.
 * - "relative": new behaviour. `curve` (still in the -30..30 range) is treated
 *   as an intensity that scales with the row length, so the same value gives a
 *   consistent visual curvature on short and long rows alike.
 */
export type CurveMode = "absolute" | "relative";

export const CURVE_MODE_ABSOLUTE: CurveMode = "absolute";
export const CURVE_MODE_RELATIVE: CurveMode = "relative";

export class RowSeatLayoutHelper {
  // UI/domain range for the curve control. Unchanged so users keep the scale
  // they already know.
  static readonly CURVE_MIN = -30;
  static readonly CURVE_MAX = 30;

  // Fraction of the row length used as the maximum deflection when curve is at
  // its extreme (|curve| === CURVE_MAX) in relative mode. Tune to taste.
  static readonly RELATIVE_MAX_RATIO = 0.5;

  static normalizeCurveValue(
    value: any,
    min = RowSeatLayoutHelper.CURVE_MIN,
    max = RowSeatLayoutHelper.CURVE_MAX
  ): number {
    const parsed = Math.trunc(Number(value) || 0);
    return Math.max(min, Math.min(parsed, max));
  }

  /**
   * Resolves the effective mode for a row. Rows saved before this change do not
   * carry a curveMode, so they default to the legacy "absolute" behaviour and
   * therefore render exactly as they were saved.
   */
  static resolveCurveMode(mode: any): CurveMode {
    return mode === CURVE_MODE_RELATIVE ? CURVE_MODE_RELATIVE : CURVE_MODE_ABSOLUTE;
  }

  /**
   * Converts the curve control value into the actual center deflection in
   * pixels, according to the mode and the row length.
   */
  static getCurveDeflection(
    curve: number,
    mode: CurveMode,
    length: number
  ): number {
    if (mode === CURVE_MODE_RELATIVE) {
      const intensity = curve / RowSeatLayoutHelper.CURVE_MAX; // -1..1
      return intensity * RowSeatLayoutHelper.RELATIVE_MAX_RATIO * length;
    }
    // Legacy: the value is the pixel deflection itself.
    return curve;
  }

  static getRowDirection(seats: any[]) {
    if (!seats || seats.length < 2) return null;

    const first = seats[0];
    const last = seats[seats.length - 1];

    // Usamos el CENTRO REAL (getCenterPoint) en vez de left/top. Tras girar la
    // fila el bounding box de cada asiento queda rotado, así que el
    // desplazamiento del top-left al centro es un vector rotado (distinto en X
    // y en Y). getCenterPoint() devuelve el centro real y hace que la pendiente
    // se calcule sobre la línea visual actual, no la original.
    const firstCenter = first.getCenterPoint();
    const lastCenter = last.getCenterPoint();

    const dx = lastCenter.x - firstCenter.x;
    const dy = lastCenter.y - firstCenter.y;

    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return null;

    const ux = dx / length;
    const uy = dy / length;

    const px = -uy;
    const py = ux;

    return { first, last, firstCenter, length, ux, uy, px, py };
  }

  static getDirectionFromAngle(firstSeat: any, angleDeg: number) {
    if (!firstSeat) return null;

    const rad = (Number(angleDeg) || 0) * (Math.PI / 180);
    const ux = Math.cos(rad);
    const uy = Math.sin(rad);

    const px = -uy;
    const py = ux;

    return { first: firstSeat, firstCenter: firstSeat.getCenterPoint(), ux, uy, px, py };
  }

  static applyParabolicCurve({
    seats,
    curve,
    spacing,
    mode,
    direction: directionOverride,
  }: {
    seats: any[];
    curve: number;
    spacing?: number;
    mode?: CurveMode;
    direction?: {
      first: any;
      firstCenter?: { x: number; y: number };
      length?: number;
      ux: number;
      uy: number;
      px: number;
      py: number;
    } | null;
  }) {
    if (!seats || seats.length < 2) return;

    const direction = directionOverride ?? this.getRowDirection(seats);
    if (!direction) return;

    const { first, ux, uy, px, py } = direction;
    const firstCenter = direction.firstCenter ?? first.getCenterPoint();

    const total = seats.length;
    const mid = (total - 1) / 2;

    const length =
      direction.length ?? this.getRowDirection(seats)?.length ?? 0;

    const effectiveSpacing =
      spacing ?? (total > 1 ? length / (total - 1) : 0);

    const spanLength =
      effectiveSpacing > 0 ? effectiveSpacing * (total - 1) : length;

    const resolvedMode = this.resolveCurveMode(mode);
    const deflection = this.getCurveDeflection(curve, resolvedMode, spanLength);

    for (let i = 0; i < total; i++) {
      const t = i * effectiveSpacing;

      // Centro objetivo del asiento i sobre la línea, anclado en el centro real
      // del primer asiento.
      const targetCenterX = firstCenter.x + ux * t;
      const targetCenterY = firstCenter.y + uy * t;

      const normalized = total > 1 ? (i - mid) / mid : 0;
      const offset = total > 2 ? -deflection * (1 - normalized * normalized) : 0;

      const desiredCenterX = targetCenterX + px * offset;
      const desiredCenterY = targetCenterY + py * offset;

      // Convertimos el centro deseado a left/top usando el offset REAL de cada
      // asiento (centro - left/top), que puede diferir por asiento si su bbox
      // está rotado. Así el CENTRO del asiento queda exactamente sobre la línea
      // sin importar la orientación de su bounding box.
      const seatCenter = seats[i].getCenterPoint();
      const offsetLeft = seats[i].left - seatCenter.x;
      const offsetTop = seats[i].top - seatCenter.y;

      seats[i].set({
        left: desiredCenterX + offsetLeft,
        top: desiredCenterY + offsetTop,
      });
      seats[i].setCoords();
    }
  }

}